import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Close, PunchClock, PushPin, PushPinOutlined, Timer } from '@mui/icons-material'
import { buildSearchString, getImagePath } from 'pageComponents/_common/helpers/generalHelperFunctions'
import { FooterSecondaryButton } from 'pageComponents/_common/HubSpotBlogSubscription'
import Loader, { TightLoader } from 'pageComponents/_common/loader'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { useAirlineFetch } from 'setup/REST'
import useStickyState from 'setup/stickyStateHook'
import styled from 'styled-components'

//The idea of the hanger div is to construct a full-width but zero-height div
//that can be relatively positioned, then stuff an absolutely positioned div
// inside that, with a particular width
const ImmediateSearchHangerDiv = styled.div`
    position: absolute;
    bottom: 0;
    height: 0px;
`
const ImmediateSearchHangerDiv2 = styled.div`
    position: relative;
    right: 0;
    top: 0;
    bottom: 0;
    max-width: 400px;
    width: 100%;
    z-index: 10; /*Position higher than the feedback div*/
    height: calc(100vh - 200px);
    min-height: 240px;
`

const ImmediateSearchFormContainer = styled.div`
    background-color: ${props => props.theme.backgroundColor};
    width: 400px;
    display: flex;
    min-height: 100px;
    margin: auto 0;
    flex-direction: column;
    border-radius: 4px 0 4px 4px;
    border: 1px solid ${props => props.theme.divisionColorBlendDarker};
    height: 100%;
    button#isearch {
        margin: 2px 4px 2px 4px;
        display: flex;
        flex-direction: row;
        align-self: center;
        align-items: center;
        height: 32px;
        justify-content: center;
        width: 50%;
        border: none;
        svg {
            margin-right: 4px;
        }
    }
`

const ImmediateSearchFormHeader = styled.div`
	display: flex;
	justify-content: space-between;
	background-color: ${props => props.theme.divisionColorBlendDarker};
    padding: 4px;
`

const ImmediateSearchContent = styled.div`
    width: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
    display:flex;
    flex-direction: column;
    * {
        margin: 3px;
    }
    hr:nth-child(even) {
        background-color: white;
    }
`

const ResultBox = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
`
const ResultBoxColumn = styled.div`
    display: flex;
    flex-direction: column;
    color: black;
`

const DescSpan = styled.span`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 0.8rem;
    width: 90%;
`

const ISHdrButton = styled.button`
    width: 25px;
    height: 25px;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    &:hover {
        color: ${props => props.theme.mainColor};
    }
`

const Left = styled.div`
    align-self: left;
    display: flex;
    align-items: flex-end;
`

const Right = styled.div`
    align-self: right;
    display: flex;
    align-items: flex-start;
    gap: 10px;
`
function NoResultsItem() {
    return (
        <ResultBox style={{height: "60px"}}>
            <ResultBoxColumn style={{ flexGrow: "0" }}>
            </ResultBoxColumn>
            <ResultBoxColumn style={{ flexGrow: "1" }}>
                <span>We couldn't find any matches. Try searching again with a different search term.</span>
            </ResultBoxColumn>
        </ResultBox>);
}

function SearchResultItem({ enrichedSearchResult, nonWebMode }) {
    if (enrichedSearchResult.Kind === 'Item') {
        return (<Link to={`/${enrichedSearchResult.Href}`}>
            <ResultBox>
                <ResultBoxColumn style={{ flexGrow: "0" }}>
                    <img height="75" src={getImagePath(enrichedSearchResult.ImageUrl)} alt={enrichedSearchResult.ItemId} />
                </ResultBoxColumn>
                <ResultBoxColumn style={{ flexGrow: "1" }}>
                    <span>{enrichedSearchResult.ItemId}</span>
                    <DescSpan>{enrichedSearchResult.Desc}</DescSpan>
                </ResultBoxColumn>
            </ResultBox>
            <hr />
        </Link>);
    } else if (enrichedSearchResult.Kind === 'CatalogItem') {
        return (<Link to={buildSearchString({ searchTerm: `CAT${enrichedSearchResult.CatalogItemId}`, nonweb: nonWebMode })}>
            <ResultBox>
                <ResultBoxColumn style={{ flexGrow: "0" }}>
                    <img height="75" src={getImagePath(enrichedSearchResult.ImageUrl)} alt={enrichedSearchResult.ItemId} />
                </ResultBoxColumn>
                <ResultBoxColumn style={{ flexGrow: "1" }}>
                    <span>{enrichedSearchResult.ItemId}</span>
                    <DescSpan>{enrichedSearchResult.Desc}</DescSpan>
                    <DescSpan>Extended Catalog Item</DescSpan>
                </ResultBoxColumn>
            </ResultBox>
            <hr />
        </Link>);
    } else {
        return <>aaaa</>;
    }
}

export default function ImmediateSearch({ debouncedSearchTerm, nonWebMode, doRegularSearch }) {
    const [openImmediateSearchModal, setOpenImmediateSearchModal] = useState(false);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [keepOpen, setKeepOpen] = useStickyState(false, 'keepImmediateSearchOpen');
    const [searchResults, setSearchResults] = useState([]);
    const { airlinePost } = useAirlineFetch();
    const loc = useLocation();

    //Implements the close when not pinned function
    useEffect(() => {
        if(!keepOpen) {
            reset();
        }
    }, [loc]);

    function toggleKeepOpen() {
        setKeepOpen(!keepOpen);
    }

    function regularSearchWrapper() {
        setSearchInProgress(false);
        setSearchResults([]);
        setOpenImmediateSearchModal(false);
        doRegularSearch();
    }

    function reset() {
        setSearchInProgress(false);
        setSearchResults(null);
        setOpenImmediateSearchModal(false);
    }

    //Search
    useEffect(() => {
        async function f() {
            setSearchInProgress(true);
            const response = await airlinePost(`${process.env.REACT_APP_API_URL}/api/Search/Immediate`, {
                Page: 1,
                PageSize: 10,
                Query: debouncedSearchTerm,
                IncludeNonWeb: nonWebMode
            });
            if (!response.ok) {
                reset();
                doRegularSearch();
            } else {
                response.json().then((res) => {
                    console.log(res);
                    setSearchResults(res);
                    setOpenImmediateSearchModal(true);
                }).then(() => setSearchInProgress(false));
            }
        }
        if (debouncedSearchTerm) {
            f();
        } else {
            reset();
        }
    }, [debouncedSearchTerm, nonWebMode]);

    return (
        <ImmediateSearchHangerDiv>
            <ImmediateSearchHangerDiv2>
                {openImmediateSearchModal && <ImmediateSearchFormContainer>
                    <ImmediateSearchFormHeader>
                        <Left>
                            <span style={{ fontStyle: "italic" }}>Find it Fast</span>
                        </Left>
                        <Right>
                            <ISHdrButton title={keepOpen ? "Keep this search box open" : "Auto close this search box" } onClick={toggleKeepOpen}>{keepOpen ? <PushPin aria-label='Keep open' /> : <PushPinOutlined aria-label='Auto close' /> }</ISHdrButton>
                            <ISHdrButton title="Close search box" onClick={() => setOpenImmediateSearchModal(false)} > <Close  /></ISHdrButton>
                        </Right>
                    </ImmediateSearchFormHeader>
                    <ImmediateSearchContent>
                        {searchResults && searchResults.Results.length === 0 && <NoResultsItem/>}
                        {searchResults && searchResults.Results.map((res, idx) => <SearchResultItem key={idx} enrichedSearchResult={res} nonWebMode={nonWebMode} />)}
                    </ImmediateSearchContent>
                    <FooterSecondaryButton title="Perform a full search" id="isearch" onClick={regularSearchWrapper} aria-label='Continue search button'>
                        <FontAwesomeIcon icon="search" color="#f6f6f6" size="sm" /> <span>Full Search</span>
                    </FooterSecondaryButton>
                </ImmediateSearchFormContainer>}
            </ImmediateSearchHangerDiv2>
        </ImmediateSearchHangerDiv>
    );
}
